import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { InspectionDeviationResultDto } from "@/types";
import Accordion from "@/components/organisms/AccordionDrawer";
import Divider from "@/components/atoms/Divider";
import { useInspectionFormat } from "@/components/organisms/InspectionModal/useInspectionFormat";
import { UnitNumberInput } from "@/components/organisms/InspectionModal/components/FormParts";
import { PER_ORDER_UNIT_CODE } from "@/components/organisms/InspectionModal/utils";
import {
  DeviationDecision,
  DeviationHeader,
  InspectionPhotos,
  Row,
} from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/TabParts";
import { DecisionCost, DecisionFields, fieldError } from "./DecisionParts";
import { DecisionPreview } from "./useDecisionPreview";
import { DeviationDecisionsForm, isPendingDeviation } from "./useDeviationDecisionsForm";

interface DeviationServiceDecisionsProps {
  /** The inspection's deviations of kind SERVICE, pending or decided. */
  deviations: InspectionDeviationResultDto[];
  form: DeviationDecisionsForm;
  preview: DecisionPreview;
}

/**
 * The deviation services to decide: confirmed, the number (corrected if need be) is charged on top at the contract's
 * price; rejected, it isn't invoiced. Decided ones are shown as in the order drawer.
 */
export default function DeviationServiceDecisions({ deviations, form, preview }: DeviationServiceDecisionsProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();

  return (
    <Accordion initiallyOpen title={t("inspections:deviations.servicesTitle")}>
      {deviations.map((deviation, index) => {
        const values = form.valuesOf(deviation);
        const isPending = isPendingDeviation(deviation);
        const unit = format.deviationUnit(deviation.unitCode);
        // A per-order service is counted once, so there's no number to correct.
        const isPerOrder = deviation.unitCode === PER_ORDER_UNIT_CODE;
        return (
          <Fragment key={deviation.id}>
            {index > 0 && <Divider margin={false} />}
            <Grid p={20} data-testid="deviation-decision-service">
              <DeviationHeader deviation={deviation} />
              <Row label={t("inspections:deviations.registeredNumber")}>
                {isPerOrder ? (
                  t("inspections:deviations.perOrder")
                ) : isPending && values.decision === "CONFIRMED" ? (
                  <UnitNumberInput
                    aria-label={t("inspections:deviations.registeredNumber")}
                    unit={unit}
                    maw={200}
                    value={values.amount}
                    onChange={(amount) => form.update(deviation, { amount })}
                    error={fieldError(form.errors, deviation, "amount", t)}
                    data-testid={`deviation-decision-${deviation.id}-amount`}
                  />
                ) : (
                  `${format.number(deviation.registeredNumber)} ${unit}`
                )}
              </Row>
              <Row label={t("inspections:drawer.unitPrice")}>
                {format.unitPrice(deviation.unitPrice, deviation.currencyType, unit)}
              </Row>
              <Row label={t("inspections:deviations.cost")}>
                <DecisionCost deviation={deviation} preview={preview} />
              </Row>
              <Row label={t("inspections:photos.label")}>
                <InspectionPhotos photoIds={deviation.photoIds} />
              </Row>
              {isPending ? (
                <DecisionFields
                  deviation={deviation}
                  values={values}
                  onChange={(patch) => form.update(deviation, patch)}
                  errors={form.errors}
                />
              ) : (
                <DeviationDecision deviation={deviation} isBuyer />
              )}
            </Grid>
          </Fragment>
        );
      })}
    </Accordion>
  );
}
